(function () {
	'use strict';

	/**
	 * @ngdoc type
	 * @name mx.shell.Wizard
	 * @module mx.shell
	 *
	 * @requires {@ref mx.shell.EventService mx.shell.EventService}
	 * @requires <a href="https://docs.angularjs.org/api/ng/service/$q" target="_blank">$q</a>
	 * @description
	 * Type that contains ultimate information about wizard.
	 * A constructor accepts `options` parameter that may contain fields to extend corresponding properties. Created like:
	 * ```javascript
	 var pageOptions = {
		id: 'wiz_1',
		title: 'Single wizard page',
		buttons: {
			finish: new WizardButton('Finish', true, function (wizardData) {
				alert('Wizard finished!\n\n\nCollected data:' +
					'\nFirst field: ' + (wizardData.firstField || 'no value') +
					'\nSecond field: ' + (wizardData.secondField || 'no value') + '\n\n');
			}),
			cancel: new WizardButton('Cancel', true),
			next: new WizardButton('Next page', false),
			back: new WizardButton('Previous page', false)
		},
		controller: 'mx.tmp.SampleWizardPageController',
		templateUrl: 'temp/wizard/wizard-page.html',
		sharedWizardData: {}
	};

	 var page = new WizardPage(pageOptions);

	 var roadmap = new WizardRoadmap({
		title: 'Sample Wizard! ',
		description: 'Some general info about wizard',
		steps: [
			new WizardRoadmapStep({
				title: 'Step 1',
				description: 'On this step we perform actions related to step 1.',
				orderNum: 1
			})
		]

	var wizardOptions = {
		name: 'Simple wizard',
		description: 'Long and pretty wizard description',
		contextDescription: 'vm.contextObject.name',
		pages: [page],
		roadmap: roadmap
	};

	var wizard = new Wizard(wizardOptions);
	 * ```
	 *
	 * @property {string} id - Gets or sets wizard's unique identifier.<br>Default value is the result of `Math.random().toString()`
	 * @property {string} name - Gets or sets wizard's name.<br>Default value is `Untitled wizard`
	 * @property {string} description - Gets or sets detailed information about wizard
	 * @property {string} contextDescription - Gets or sets information about wizard's context
	 * @property {function} displayContextFn - Gets or sets a function to display wizard's context
	 * @property {mx.shell.WizardPage[]} pages - Provides access to {@ref mx.shell.WizardPage wizard pages}.<br>Default value is `[]`
	 * @property {mx.shell.Wizard[]} extraWizards - Gets additional {@ref mx.shell.Wizard wizards} to be processed within current one.<br>Default value is `[]`
	 * @property {mx.shell.WizardRoadmap} roadmap - Gets or sets wizard's {@ref mx.shell.WizardRoadmap roadmap}
	 * It contains information about wizard steps and all related info
	 * @property {int} positionInBaseWizard - Gets or sets wizard's position as an extra wizard.<br>
	 * Applies only if a wizard is a part of an Extra wizards array of another (base) wizard.<br>Default value is `0`
	 * @property {mx.shell.WizardPage} currentPage - Gets or sets current {@ref mx.shell.WizardPage wizard page}
	 * @property {mx.shell.WizardPage[]} allWizardPages gets all {@ref mx.shell.WizardPage wizard pages} including self pages and extra wizards pages
	 * @property {mx.shell.WizardRoadmapStep[]} allWizardSteps gets all {@ref mx.shell.WizardRoadmapStep wizard steps} including self steps and extra wizards steps
	 */
	angular.module('mx.shell').factory('mx.shell.Wizard', [
		'mx.shell.EventService',
		'$q',
		'$timeout',
		function wizardFactory(eventService, $q, $timeout) {
			function Wizard(options) {
				options = options || {};
				var that = this;
				var currentStep = 0;
				that.sourceState = {
					Submiting: 1,
					Ok: 2,
					Failed: 3,
				};

				Object.defineProperty(that, 'id', {
					writable: true,
					value: Math.random().toString(),
				});
				Object.defineProperty(that, 'name', {
					writable: true,
					value: 'Untitled wizard',
				});
				Object.defineProperty(that, 'description', { writable: true });
				Object.defineProperty(that, 'contextDescription', { writable: true });
				Object.defineProperty(that, 'displayContextFn', { writable: true });
				Object.defineProperty(that, 'pages', {
					writable: true,
					value: [],
				});
				Object.defineProperty(that, 'roadmap', { writable: true });
				Object.defineProperty(that, 'errors', {
					writable: true,
					value: [],
				});
				Object.defineProperty(that, 'positionInBaseWizard', {
					writable: true,
					value: [],
				});
				Object.defineProperty(that, 'resultPage', {
					writable: true,
					value: options.resultPage,
				});

				Object.defineProperty(that, 'currentStep', {
					get: function () {
						return currentStep;
					},
				});

				Object.defineProperty(that, 'finishDefer', {
					writable: true,
					enumerable: true,
					value: (options.wizardView && options.wizardView.finishDefer) || $q.defer(),
				});

				Object.defineProperty(that, 'managers', {
					writable: false,
					enumerable: true,
					value: [],
				});

				that.finishDefer.promise
					.then(function () {
						eventService.dispatchEvent(mx.shell.EventCodes.WizardClose, {
							wizard: that,
							canceled: false,
						});
					})
					.catch(function () {
						eventService.dispatchEvent(mx.shell.EventCodes.WizardClose, {
							wizard: that,
							canceled: true,
						});
					});

				that.wizardSourceState = that.sourceState.Ok;
				Object.defineProperty(that, 'wizardSourceState', { writable: true });

				/**
				 * @ngdoc method
				 * @name mx.shell.Wizard#setCurrentStep
				 * @module mx.shell
				 *
				 * @description
				 * Sets a step identified by a specified id as current step and serves all the work related to other steps.
				 * If no step with specified id found nothing is done
				 *
				 * @param {string} stepId - An {@ref mx.shell.WizardRoadmapStep#id id of a step} that should be set as current.
				 */
				that.setCurrentStep = function (stepId) {
					var newStep = null;

					that.allWizardSteps.some(function (step, index) {
						if (step.id === stepId) {
							newStep = step;
							currentStep = index;
							return true;
						}
						return false;
					});

					if (!newStep) {
						return;
					}

					that.allWizardSteps.forEach(function (step) {
						step.isCurrent = step.id === stepId;
						step.passed = step.orderNum < newStep.orderNum;
					});
				};

				var currentPage;

				Object.defineProperty(that, 'currentPage', {
					get: function () {
						return currentPage;
					},
					set: function (value) {
						currentPage = value;
						if (currentPage && that.roadmap) {
							that.setCurrentStep(value.wizardStepId);
						}
					},
				});

				var extraWizards = [];

				Object.defineProperty(that, 'extraWizards', {
					get: function () {
						return extraWizards;
					},
				});

				var allWizardPages = [];

				Object.defineProperty(that, 'allWizardPages', {
					get: function () {
						return allWizardPages;
					},
				});

				var allWizardSteps = [];

				Object.defineProperty(that, 'allWizardSteps', {
					get: function () {
						return allWizardSteps;
					},
				});

				var hideFinishButton = false;

				Object.defineProperty(that, 'hideFinishButton', {
					get: function () {
						return hideFinishButton;
					},
					set: function (newValue) {
						hideFinishButton = newValue;
						that.adjustButtonsTitles(allWizardPages);
						// allWizardPages[allWizardPages.length - 1].buttons.finish.isVisible = !newValue;
					},
				});

				/* Don't set currentPage while steps are not collected */
				var optionsCurrentPage = options.currentPage;
				delete options.currentPage;

				that = angular.extend(that, options);

				if (Array.isArray(that.pages)) {
					// Sorting pages by orderNum
					that.pages.sort(function (page1, page2) {
						return page1.orderNum - page2.orderNum;
					});
				}

				allWizardPages = collectPages();
				allWizardSteps = collectSteps();

				if (optionsCurrentPage) {
					that.currentPage = optionsCurrentPage;
				}
				// Setting current page if not set
				if (!that.currentPage && Array.isArray(that.pages) && that.pages.length > 0) {
					that.currentPage = that.pages[0];
				}

				function collectPages() {
					var result = that.pages;

					that.extraWizards.forEach(function (wizard) {
						result = result.concat(wizard.pages);
					});

					return result;
				}

				function stepFilter(step) {
					return !step.toBeSkipped;
				}

				function collectSteps() {
					if (!that.roadmap || !that.roadmap.steps || !that.roadmap.steps.length) {
						return [];
					}

					var result = that.roadmap.steps.filter(stepFilter);

					that.extraWizards.forEach(function (wizard) {
						result = result.concat(wizard.roadmap.steps.filter(stepFilter));
					});

					return result;
				}

				that.updateSteps = function () {
					allWizardSteps = collectSteps();
				};

				/**
				 * @ngdoc method
				 * @name mx.shell.Wizard#addExtraWizard
				 * @module mx.shell
				 * @description Adds a new wizard to current wizard {@ref mx.shell.Wizard.extraWizards extraWizards}.
				 * New wizard's {@ref mx.shell.Wizard.positionInBaseWizard positionInBaseWizard} property is taken into account.
				 * @param {mx.shell.Wizard} wizard - an extra wizard to add
				 */
				that.addExtraWizard = function (wizard, removeDuplicates) {
					if (typeof wizard !== typeof that) {
						throw new Error('Invalid argument ' + typeof that + ' expected, ' + typeof wizard + ' passed');
					}

					if (removeDuplicates) {
						that.setExtraWizards(
							that.extraWizards.filter(function (extraWizard) {
								return extraWizard.id !== wizard.id;
							})
						);
					}

					var newWizards = that.extraWizards.concat([wizard]).sort(function (wizard1, wizard2) {
						return (wizard1.positionInBaseWizard || 0) - (wizard2.positionInBaseWizard || 0);
					});

					that.setExtraWizards(newWizards);
				};

				/**
				 * @ngdoc method
				 * @name mx.shell.Wizard#removeExtraWizard
				 * @module mx.shell
				 * @description Removes new wizard from current wizard's {@ref mx.shell.Wizard.extraWizards extraWizards}
				 * @param {mx.shell.Wizard} wizard - an extra wizard to remove
				 */
				that.removeExtraWizard = function (wizard) {
					that.setExtraWizards(
						that.extraWizards.filter(function (extraWizard) {
							return extraWizard !== wizard;
						})
					);
				};

				/**
				 * @ngdoc method
				 * @name mx.shell.Wizard#setExtraWizards
				 * @module mx.shell
				 * @description Sets new {@ref mx.shell.Wizard.extraWizards extraWizards} value
				 * @param {mx.shell.Wizard[]} wizards - new extra wizards array
				 */
				that.setExtraWizards = function (wizards) {
					if (extraWizards === wizards) {
						return;
					}

					extraWizards = wizards || [];

					var lastBasePage = getLastPage(that);

					lastBasePage.buttons.finish.isVisible = !extraWizards.length;
					lastBasePage.buttons.next.isVisible = Boolean(extraWizards.length);
					if (extraWizards[0]) {
						lastBasePage.buttons.next.title = extraWizards[0].pages[0].title;
					}

					var extraPagesIndex = that.pages.length;
					var extraStepsIndex = that.roadmap.steps.length;

					extraWizards.forEach(function (wizard, index) {
						var firstPageButtons = wizard.pages[0].buttons;
						var lastPageButtons = getLastPage(wizard).buttons;

						firstPageButtons.back.isVisible = true;
						firstPageButtons.back.title = index === 0 ? lastBasePage.title : getLastPage(extraWizards[index - 1]).title;

						var isLastWizard = index === extraWizards.length - 1;

						lastPageButtons.finish.isVisible = isLastWizard;
						lastPageButtons.next.isVisible = !isLastWizard;

						wizard.pages.forEach(function (page, index) {
							page.orderNum = extraPagesIndex + index + 1;
						});

						wizard.roadmap.steps.forEach(function (step, index) {
							step.orderNum = extraStepsIndex + index + 1;
						});

						extraPagesIndex += wizard.pages.length;
						extraStepsIndex += wizard.roadmap.steps.length;

						if (isLastWizard) {
							lastPageButtons.finish.title = lastBasePage.buttons.finish.title;
						} else {
							lastPageButtons.next.title = extraWizards[index + 1].pages[0].title;
						}
					});

					allWizardPages = collectPages();
					allWizardSteps = collectSteps();
				};

				function wizardContainsPages(wizard, pages) {
					var result = true;

					pages.forEach(function (page) {
						result =
							result &&
							wizard.pages.some(function (wizardPage) {
								return wizardPage === page;
							});
					});

					return result;
				}

				function isSameWizardPages(page1, page2) {
					var pages = [page1, page2];

					if (wizardContainsPages(that, pages)) {
						return true;
					}

					return that.extraWizards.some(function (wizard) {
						return wizardContainsPages(wizard, pages);
					});
				}

				function _validateWizardPageNext() {
					return that.currentPage && that.currentPage.buttons && that.currentPage.buttons.next && that.currentPage.buttons.next.isVisible
						? _validateWizardPage(that.currentPage.buttons.next)
						: $q.when(true);
				}

				function _validateWizardPageFinish() {
					return that.currentPage && that.currentPage.buttons && that.currentPage.buttons.finish && that.currentPage.buttons.finish.isVisible
						? _validateWizardPage(that.currentPage.buttons.finish)
						: $q.when(true);
				}

				function _validateWizardPage(button) {
					if (button && typeof button.validate === 'function') {
						return button.validate();
					}

					return $q.when(true);
				}

				/**
				 * @ngdoc method
				 * @name mx.shell.Wizard#containsPage
				 * @instance
				 * @module mx.shell
				 * @description Check if wizard contains some page
				 * @param {WizardPage} page - some wizard page
				 *
				 * @returns {boolean} contains or not
				 */
				that.containsPage = function (page) {
					return allWizardPages.includes(page) || that.resultPage === page;
				};

				/**
				 * @ngdoc method
				 * @name mx.shell.Wizard#containsPageView
				 * @instance
				 * @module mx.shell
				 * @description Check if wizard contains some page view
				 * @param {mx.shell.WizardView} pageView - some wizard page view
				 *
				 * @returns {boolean} contains or not
				 */
				that.containsPageView = function (pageView) {
					return (
						allWizardPages.some(function (page) {
							return page.view === pageView;
						}) ||
						(that.resultPage && that.resultPage.view === pageView)
					);
				};

				/**
				 * @ngdoc method
				 * @name mx.shell.Wizard#goToNextPage
				 * @module mx.shell
				 * @description Performs move to next wizard page
				 */
				that.goToNextPage = function () {
					var currentPage = that.currentPage;
					_validateWizardPageNext().then(function (isValid) {
						// in case current page was changed during validation than not go to next page (example by onChangeSkippedPages)
						if (currentPage !== that.currentPage) {
							return;
						}
						if (isValid) {
							if (that.currentPage && that.currentPage.buttons) {
								callCallback(that.currentPage.buttons.next.callback);
							}

							var pageToBeCurrent = getNextPage(getCurrentPageIndex(), 1);

							if (pageToBeCurrent) {
								if (isSameWizardPages(pageToBeCurrent, that.currentPage)) {
									pageToBeCurrent.sharedWizardData = that.currentPage.sharedWizardData;
								}
								that.currentPage = pageToBeCurrent;
							}
						}
					});
				};

				/**
				 * @ngdoc method
				 * @name mx.shell.Wizard#goToPreviousPage
				 * @module mx.shell
				 * @description Performs move to previous wizard page (back)
				 */
				that.goToPreviousPage = function () {
					if (that.currentPage && that.currentPage.buttons) {
						callCallback(that.currentPage.buttons.back.callback);
					}

					var pageToBeCurrent = getNextPage(getCurrentPageIndex(), -1);

					if (pageToBeCurrent) {
						if (isSameWizardPages(pageToBeCurrent, that.currentPage)) {
							pageToBeCurrent.sharedWizardData = that.currentPage.sharedWizardData;
						}
						that.currentPage = pageToBeCurrent;
					}
				};

				/**
				 * @ngdoc method
				 * @name mx.shell.Wizard#cancelWizard
				 * @module mx.shell
				 * @description Cancels wizard and raises {@ref mx.shell.EventCodes#WizardClose WizardClose event}
				 */
				that.cancelWizard = function () {
					if (that.currentPage && that.currentPage.buttons) {
						callCallback(that.currentPage.buttons.cancel.callback).then(function () {
							that.finishDefer.reject(mx.noLogCatchMessage('cancel wizard'));
						});
					}
				};

				/**
				 * @ngdoc method
				 * @name mx.shell.Wizard#finishWizard
				 * @module mx.shell
				 * @description Performs finish wizard actions and raises
				 * {@ref mx.shell.EventCodes#WizardClose WizardClose event} if everything is ok
				 */
				that.finishWizard = function () {
					_validateWizardPageFinish().then(function (isValid) {
						if (isValid && that.currentPage && that.currentPage.buttons) {
							that.wizardSourceState = that.sourceState.Submiting;

							if (that.currentPage.buttons.finish) {
								that.currentPage.buttons.finish.isDisabled = true;
							}
							if (that.currentPage.buttons.cancel) {
								that.currentPage.buttons.cancel.isDisabled = true;
							}

							if (that.currentPage === that.resultPage) {
								callCallback(that.currentPage.buttons.finish.callback, that.currentPage).then(function (result) {
									processLastCallbackResult(true, result);
								});
								return;
							}

							var lastPage = getLastPage(that);

							callCallback(lastPage.buttons.finish.callback, lastPage).then(
								function (result) {
									var lastPromise = $q.when();

									that.extraWizards.forEach(function (wizard) {
										var page = getLastPage(wizard);

										lastPromise = lastPromise.then(function () {
											return callCallback(page.buttons.finish.callback, page).then(null, function () {
												that.wizardSourceState = that.sourceState.Ok;
											});
										});
									});

									lastPromise.then(function () {
										if (that.resultPage && !that.resultPage.toBeSkipped) {
											that.currentPage = that.resultPage;
											that.wizardSourceState = that.sourceState.Ok;
										} else {
											//TODO: may be it is necessary to process data from all extra-wizards instead of data just from the first main wizard
											processLastCallbackResult(true, result);
										}
									});
								},
								function (result) {
									processLastCallbackResult(false, result);
								}
							);
						}
					});
				};

				that.adjustButtonsTitles = function (pages) {
					pages.forEach(function (page, index, pages) {
						var nextTitle = getNextTitle(pages, page.orderNum);
						var backTitle = getBackTitle(pages, page.orderNum);

						page.buttons.finish.isVisible = !nextTitle && !hideFinishButton;
						page.buttons.next.isVisible = Boolean(nextTitle);
						page.buttons.next.title = nextTitle || page.buttons.next.title;

						page.buttons.back.isVisible = Boolean(backTitle);
						page.buttons.back.title = backTitle || page.buttons.back.title;
					});
				};

				that.registerWizardManager = function (wizardManagerController) {
					return that.managers.push({
						id: that.managers.length + 1,
						controller: wizardManagerController,
						pagesToSkip: [],
					});
				};

				that.getManager = function (managerId) {
					return that.managers[managerId - 1];
				};

				that.getAllPagesToSkip = function () {
					return that.managers.reduce((allPages, manager) => allPages.concat(manager.pagesToSkip), []);
				};

				that.skipPagesByManager = function (managerId, managerPagesToSkip) {
					that.getManager(managerId).pagesToSkip = Array.isArray(managerPagesToSkip) ? managerPagesToSkip : [];
					var pagesToSkip = that.getAllPagesToSkip();
					var pages = that.allWizardPages;
					var steps = that.roadmap ? that.roadmap.steps : [];
					var changeButtonsTitles = false;

					pages.forEach(function (page) {
						var toBeSkipped = Array.isArray(pagesToSkip) && pagesToSkip.indexOf(page.key) !== -1;

						if (page.toBeSkipped !== toBeSkipped) {
							page.toBeSkipped = toBeSkipped;
							steps.some(function (step) {
								if (step.key === page.key) {
									step.toBeSkipped = toBeSkipped;
								}
							});
							changeButtonsTitles = true;
						}
					});

					if (that.resultPage) {
						var page = that.resultPage;
						var toBeSkipped = Array.isArray(pagesToSkip) && pagesToSkip.indexOf(page.key) !== -1;
						if (page.toBeSkipped !== toBeSkipped) {
							page.toBeSkipped = toBeSkipped;
						}
					}

					if (changeButtonsTitles) {
						that.adjustButtonsTitles(pages);
					}

					that.updateSteps();

					if (that.currentPage.toBeSkipped) {
						that.goToNextPage();
					}
				};

				function processLastCallbackResult(close, result) {
					if (close === true) {
						$timeout(function () {
							that.finishDefer.resolve(result);
						});
					} else {
						var errorData = result?.data;
						var errorMsg = typeof errorData === 'string' ? errorData : errorData?.Message;
						that.errors = [{ message: errorMsg, type: 'error', active: true, transient: true }];
					}

					if (that.currentPage.buttons.finish) {
						that.currentPage.buttons.finish.isDisabled = false;
					}
					if (that.currentPage.buttons.cancel) {
						that.currentPage.buttons.cancel.isDisabled = false;
					}

					that.wizardSourceState = that.sourceState.Ok;
				}

				function getLastPage(wizard) {
					return wizard.pages[wizard.pages.length - 1];
				}

				function callCallback(callback, page) {
					return $q.when(typeof callback === 'function' ? callback((page || that.currentPage).sharedWizardData) : true);
				}

				function getNextPage(currentIndex, shiftBy) {
					var nextIndex = currentIndex + shiftBy;
					var nextPage;

					if (nextIndex < that.pages.length || !that.extraWizards.length) {
						nextPage = that.pages[nextIndex];
					} else {
						var extraIndex = that.pages.length;

						that.extraWizards.some(function (item) {
							extraIndex += item.pages.length;
							if (nextIndex < extraIndex) {
								nextPage = item.pages[nextIndex - (extraIndex - item.pages.length)];
								return true;
							}
							return false;
						});
					}

					if (!nextPage) {
						return null;
					}

					return nextPage.toBeSkipped ? getNextPage(nextIndex, shiftBy) : nextPage;
				}

				function getCurrentPageIndex() {
					var foundIndex = -2;

					if (that.currentPage && Array.isArray(that.pages)) {
						that.pages.some(function (page, index) {
							if (page.id === that.currentPage.id) {
								foundIndex = index;
								return true;
							}
							return false;
						});
					}

					if (foundIndex === -2 && that.extraWizards.length) {
						var extraIndex = that.pages.length;

						that.extraWizards.some(function (wizard) {
							wizard.pages.some(function (page, index) {
								if (page.id === that.currentPage.id) {
									foundIndex = extraIndex + index;
									return true;
								}
								return false;
							});

							if (foundIndex === -2) {
								extraIndex += wizard.pages.length;
								return false;
							}

							return true;
						});
					}

					return foundIndex;
				}

				function getNextTitle(pages, currentOrderNum) {
					var nextAvailablePage = pages.filter(function (page) {
						return !page.toBeSkipped && page.orderNum > currentOrderNum;
					})[0];

					return nextAvailablePage ? nextAvailablePage.title : null;
				}

				function getBackTitle(pages, currentOrderNum) {
					var previousAvailablePages = pages.filter(function (page) {
						return !page.toBeSkipped && page.orderNum < currentOrderNum;
					});

					var previousAvailablePage = previousAvailablePages[previousAvailablePages.length - 1];

					return previousAvailablePage ? previousAvailablePage.title : null;
				}
			}

			return Wizard;
		},
	]);
})();
